import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsent.css';

const STORAGE_KEY = 'cosmoCookieConsent';

// Public helpers — call these from anywhere that needs to gate non-essential
// cookies/analytics on the user's choice.
//
//   getCookieConsent() → 'all' | 'essential' | null
//
//   • 'all'        → user accepted functional + analytics + (future) advertising
//   • 'essential'  → user declined non-essential cookies
//   • null         → user hasn't chosen yet (treat as essential-only)
//
// hasNonEssentialConsent() is the typical check before loading analytics.
export function getCookieConsent() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function hasNonEssentialConsent() {
  return getCookieConsent() === 'all';
}

export function setCookieConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore quota / disabled storage */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('cosmo:cookie-consent-change', { detail: value })
    );
  }
}

// Opens the banner again so the user can change their choice later.
// Wired to the footer button and the link in /cookies.
export function openCookieSettings() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('cosmo:open-cookie-settings'));
  }
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) setOpen(true);
    const handler = () => setOpen(true);
    window.addEventListener('cosmo:open-cookie-settings', handler);
    return () => window.removeEventListener('cosmo:open-cookie-settings', handler);
  }, []);

  const choose = useCallback((value) => {
    setCookieConsent(value);
    setOpen(false);
  }, []);

  if (!open) return null;

  return (
    <div
      className="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
    >
      <div className="cookie-banner__inner">
        <div className="cookie-banner__text">
          <h2 id="cookie-banner-title" className="cookie-banner__title">
            Cookies on CosmoEstates
          </h2>
          <p id="cookie-banner-desc" className="cookie-banner__desc">
            We use strictly necessary cookies to make the site work. With your consent we also
            use functional and analytics cookies to remember preferences (such as "recently
            viewed" listings) and to improve the service. You can change your choice at any
            time. Read more in our <Link to="/cookies">Cookie Policy</Link>.
          </p>
        </div>
        <div className="cookie-banner__actions">
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--ghost"
            onClick={() => choose('essential')}
          >
            Essential only
          </button>
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--primary"
            onClick={() => choose('all')}
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
